'use client';
// ────────────────────────────────────────────────────────────
// MessageActions — shared AI-message action row (copy / 👍 / 👎 / ⋯).
//
// Renders the row beneath an AI bubble only. Caller is responsible
// for the `msg.role === 'ai' && msg.text.trim().length > 0` gate
// so this component stays pure presentation.
//
// State + handlers come from `useMessageActions` (see lib/useMessageActions.ts)
// — both the desktop ChatDrawer and the mobile /m/q page pass the
// same `actions` object here so the two surfaces never diverge.
//
// Render `<MessageActionsStyles />` ONCE near the top of the chat
// container (beside backdrop / root) so the `.qio-action-*` class
// rules are in scope. Importing `<MessageActions />` alone doesn't
// ship the CSS — that's intentional: multiple action rows per turn
// should not each re-inject the same `<style>` block.
// ────────────────────────────────────────────────────────────
import type { MessageActionsHandle } from '@/lib/useMessageActions';

interface MessageActionsProps {
    actions: MessageActionsHandle;
    messageIndex: number;
    messageId?: string;
    messageText: string;
    // Copy payload may differ from messageText when the surface has
    // reconstructed a rich copy string from the blocks tree (desktop
    // does this for hero+budget turns). When absent, falls back to
    // messageText.
    copyPayload?: string;
    className?: string;
}

export function MessageActions({
    actions,
    messageIndex,
    messageId,
    messageText,
    copyPayload,
    className,
}: MessageActionsProps) {
    const reaction = messageId ? (actions.messageReactions[messageId] ?? null) : null;
    const isLiked = reaction === 'like';
    const isDisliked = reaction === 'dislike';
    const isMenuOpen = actions.openMenuIdx === messageIndex;
    const payload = copyPayload ?? messageText;

    return (
        <div
            className={`flex items-center pt-1${className ? ` ${className}` : ''}`}
            style={{ direction: 'ltr', gap: '6px' }}
        >
            <button
                type="button"
                aria-label="نسخ"
                onClick={() => actions.handleCopyMessage(payload)}
                className="qio-action-btn"
            >
                {/* Overlapping rounded squares — tighter ChatGPT-style copy */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="8" y="8" width="12" height="12" rx="3"></rect>
                    <rect x="3" y="3" width="12" height="12" rx="3"></rect>
                </svg>
            </button>
            <button
                type="button"
                aria-label="إعجاب"
                aria-pressed={isLiked}
                onClick={() => actions.handleToggleReaction('like', messageId, messageText)}
                className={`qio-action-btn qio-reaction${isLiked ? ' qio-like-active' : ''}`}
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill={isLiked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M7 10v11" />
                    <path d="M7 10l4-8a1.5 1.5 0 0 1 2.81 1.04L13 7h5.83a2 2 0 0 1 1.95 2.44l-1.83 8A2 2 0 0 1 17 19H7" />
                </svg>
            </button>
            <button
                type="button"
                aria-label="غير مناسب"
                aria-pressed={isDisliked}
                onClick={() => actions.handleToggleReaction('dislike', messageId, messageText)}
                className={`qio-action-btn qio-reaction${isDisliked ? ' qio-dislike-active' : ''}`}
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill={isDisliked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 14V3" />
                    <path d="M17 14l-4 8a1.5 1.5 0 0 1-2.81-1.04L11 17H5.17a2 2 0 0 1-1.95-2.44l1.83-8A2 2 0 0 1 7 5h10" />
                </svg>
            </button>
            <div className="qio-action-menu">
                <button
                    type="button"
                    aria-label="المزيد"
                    aria-haspopup="menu"
                    aria-expanded={isMenuOpen}
                    onClick={() => actions.setOpenMenuIdx(prev => (prev === messageIndex ? null : messageIndex))}
                    className="qio-action-btn"
                >
                    {/* MoreHorizontal — three dots (inline SVG, no lucide dep) */}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                        <circle cx="5" cy="12" r="1.6" />
                        <circle cx="12" cy="12" r="1.6" />
                        <circle cx="19" cy="12" r="1.6" />
                    </svg>
                </button>
                {isMenuOpen && (
                    <div role="menu" className="qio-action-menu-popover">
                        <button
                            type="button"
                            role="menuitem"
                            className="qio-action-menu-item"
                            onClick={() => { actions.handleSaveMemory(messageText); actions.setOpenMenuIdx(null); }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 5v14" />
                                <path d="M5 12h14" />
                            </svg>
                            تذكّر هذا
                        </button>
                        <button
                            type="button"
                            role="menuitem"
                            className="qio-action-menu-item"
                            onClick={() => { actions.handleRemoveMemory(messageText); actions.setOpenMenuIdx(null); }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M5 12h14" />
                            </svg>
                            انسَ هذا
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Scoped CSS for the action row ──
// Emits the .qio-action-* rules used by <MessageActions /> and by
// the document-level outside-click listener inside useMessageActions
// (which queries `.qio-action-menu` to decide whether a click counts
// as "outside"). Render this component ONCE per chat surface, near
// the root — not per-message — so the same rules aren't re-injected
// on every AI bubble.
export function MessageActionsStyles() {
    return (
        <style>{`
            .qio-action-btn {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 24px;
                height: 24px;
                padding: 2px;
                background: transparent;
                border: none;
                border-radius: 6px;
                color: #9ca3af;
                opacity: 0.6;
                cursor: pointer;
                transition: all 150ms ease;
                line-height: 0;
            }
            .qio-action-btn:hover { opacity: 1; color: #374151; }
            .qio-action-btn.qio-reaction:hover { transform: scale(1.08); }
            .qio-action-btn.qio-like-active {
                color: #facc15;
                opacity: 1;
                filter: drop-shadow(0 0 3px rgba(250,204,21,0.55));
            }
            .qio-action-btn.qio-like-active:hover { color: #facc15; }
            .qio-action-btn.qio-dislike-active {
                color: #ef4444;
                opacity: 1;
            }
            .qio-action-btn.qio-dislike-active:hover { color: #ef4444; }
            .qio-action-menu {
                position: relative;
                display: inline-flex;
            }
            .qio-action-menu-popover {
                position: absolute;
                top: calc(100% + 4px);
                left: 0;
                z-index: 50;
                min-width: 180px;
                background: #ffffff;
                border: 1px solid #e5e7eb;
                border-radius: 8px;
                box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
                padding: 6px;
                direction: rtl;
                text-align: right;
            }
            .qio-action-menu-item {
                display: flex;
                align-items: center;
                gap: 8px;
                width: 100%;
                padding: 7px 10px;
                background: transparent;
                border: none;
                border-radius: 6px;
                color: #374151;
                font-size: 13px;
                cursor: pointer;
                transition: background 120ms ease;
                text-align: right;
            }
            .qio-action-menu-item:hover { background: #f3f4f6; }
            .qio-action-menu-item svg { flex-shrink: 0; color: #6b7280; }
        `}</style>
    );
}
